import React, { useState } from 'react';

import { PencilSimpleIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { getModels } from 'api/models';

import { deleteAgentArea, deleteAgentMcp, deleteAgentOffice, deleteAgentTag, getAgent, getAgents, updateAgent } from 'api/agents';

import AgentAddAreasPopup from 'components/Agents/AgentAddAreasPopup';
import AgentAddMCPPopup from 'components/Agents/AgentAddMCPPopup';
import AgentAddOfficesPopup from 'components/Agents/AgentAddOfficesPopup';
import AgentAddTagPopup from 'components/Agents/AgentAddTagPopup';
import Container from 'components/Container';
import TextInput from 'components/Form/TextInput';
import SelectInput from 'components/Form/SelectInput';
import TextareaInput from 'components/Form/TextareaInput';
import Page from 'components/Layout/Page';

export default function Edit() {

    const [sidebar] = useState(false);
    const { agentId } = useParams();
    const [addMcpPopup, setAddMcpPopup] = useState(false);
    const [addTagPopup, setAddTagPopup] = useState(false);
    const [addAreaPopup, setAddAreaPopup] = useState(false);
    const [addOfficePopup, setAddOfficePopup] = useState(false);
    const cache = useQueryClient();

    const { data: agent, isLoading } = useQuery({
        queryKey: ['agents', agentId],
        queryFn: async () => await getAgent({
            id: agentId
        }),
    });

    const { data: modelsData } = useQuery({
        queryKey: ['models'],
        queryFn: async () => await getModels({}),
    });

    const modelOptions = modelsData
        ?.filter(m => m.enabled && m.type === 'model')
        ?.map(m => ({ value: m._id, label: m.model })) || [];

    const { data: agentsData } = useQuery({
        queryKey: ['agents'],
        queryFn: async () => await getAgents({}),
    });
    const parentOptions = [
        { value: '', label: 'Nessun parent' },
        ...(agentsData?.filter(a => a._id !== agentId)?.map(a => ({ value: a._id, label: a.name })) || [])
    ];

    const { mutate: update } = useMutation({
        mutationKey: ['updateAgent'],
        mutationFn: updateAgent,
        onSuccess: () => {
            cache.invalidateQueries(['agents', agentId]);
        },
        onError: (error) => {
            console.error("Error updating agent:", error);
        }
    });

    //#region Area funzioni di delete
    const { mutate: deleteArea } = useMutation({
        mutationKey: ['deleteAgentArea'],
        mutationFn: deleteAgentArea,
        onSuccess: () => {
            cache.invalidateQueries(['agents', agentId]);
        },
        onError: (error) => {
            console.error("Error removing Area from agent:", error);
        }
    });
    function deleteAreaHandler(id) {
        return deleteArea({
            id: agentId,
            areaId: id
        });
    }

    const { mutate: deleteMcp } = useMutation({
        mutationKey: ['deleteAgentMcp'],
        mutationFn: deleteAgentMcp,
        onSuccess: () => {
            cache.invalidateQueries(['agents', agentId]);
        },
        onError: (error) => {
            console.error("Error removing MCP from agent:", error);
        }
    });
    function deleteMcpHandler(id) {
        return deleteMcp({
            id: agentId,
            mcpId: id
        });
    }

    const { mutate: deleteTag } = useMutation({
        mutationKey: ['deleteAgentTag'],
        mutationFn: deleteAgentTag,
        onSuccess: () => {
            cache.invalidateQueries(['agents', agentId]);
        },
        onError: (error) => {
            console.error("Error removing tag from agent:", error);
        }
    });
    function deleteTagHandler(id) {
        return deleteTag({
            id: agentId,
            tagId: id
        });
    }

    const { mutate: deleteOffice } = useMutation({
        mutationKey: ['deleteAgentOffice'],
        mutationFn: deleteAgentOffice,
        onSuccess: () => {
            cache.invalidateQueries(['agents', agentId]);
        },
        onError: (error) => {
            console.error("Error removing office from agent:", error);
        }
    });
    function deleteOfficeHandler(id) {
        return deleteOffice({
            id: agentId,
            officeId: id
        });
    }
    //#endregion

    function onSubmitHandler(formData) {

        let data = Object.fromEntries(formData);

        // Rimuovi le proprietà vuote
        data = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== ''));

        return update({
            data,
            id: agentId
        });
    }

    function onChangeImageHandler(e) {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                return update({
                    data: { image: file },
                    id: agentId
                });
            };
            reader.readAsDataURL(file);
        }
    }

    return (
        <>
            <Page isLoading={isLoading}>

                <form action={onSubmitHandler}>
                    <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-y-auto ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                        <div className={`z-10 fixed top-0 w-[calc(100%-320px)] left-[320px] right-0 border-b border-black/6 py-3 px-6 flex justify-between items-center backdrop-blur-sm`}>
                            <div className='flex gap-2 items-center'>
                                <p className='text-lg leading-none'>{agent?.name}</p>
                            </div>

                            <div>
                                <button type="submit" className='bg-black hover:bg-greydark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors cursor-pointer duration-200'>
                                    Salva modifiche
                                </button>
                            </div>
                        </div>

                        <Container>
                            <div className='flex items-start gap-10 pt-20'>
                                <div className='w-[240px]'>
                                    <div className='w-full px-6 py-12 bg-verylight border border-black/6 rounded-sm flex flex-col items-center justify-center'>
                                        <div className='relative'>
                                            {agent?.image ?
                                                <img src={agent?.image} alt={agent?.name} className='w-28 h-28 object-cover rounded-full mb-4' />
                                                :
                                                <div className='w-28 h-28 bg-white border border-black/6 rounded-full mb-4' />
                                            }
                                            <div className='absolute w-7 h-7 bg-greydark rounded-full absolute top-1 right-1 flex items-center justify-center hover:bg-black cursor-pointer transition-colors duration-200'>
                                                <input type="file" className='absolute appearance-none top-0 left-0 bottom-0 right-0 opacity-0' onChange={(e) => onChangeImageHandler(e)} />
                                                <PencilSimpleIcon size={17} weight="bold" className='text-white m-1' />
                                            </div>
                                        </div>
                                        <p className='text-lg text-center mb-2'>{agent?.name}</p>
                                        <p className='text-sm text-muted text-center'>{agent?.description}</p>
                                    </div>
                                    {agent?.updatedAt &&
                                        <p className='text-xs text-muted pt-4 text-center'>Ultima modifica: {format(new Date(agent.updatedAt), 'dd/MM/yyyy HH:mm')}</p>
                                    }
                                </div>

                                <div className='flex-1 scroll-auto flex flex-col gap-4 pb-20'>
                                    <TextInput
                                        label="Titolo"
                                        placeholder="Nome"
                                        name="name"
                                        defaultValue={agent?.name}
                                    />
                                    <TextInput
                                        label="Identificatore"
                                        placeholder="Slug (automatico se vuoto)"
                                        name="slug"
                                        defaultValue={agent?.slug}
                                    />
                                    <div className='md:flex gap-5'>
                                        <SelectInput
                                            label="Tipologia"
                                            name="type"
                                            defaultValue={agent?.type}
                                            options={[
                                                { label: 'Data to data', value: 'data-to-data' },
                                                { label: 'Images to data', value: 'images-to-data' },
                                                { label: 'Images to ddt', value: 'images-to-ddt' },
                                                { label: 'OCR to data', value: 'ocr-to-data' },
                                                { label: 'Fine-tuning dataset creator', value: 'fine-tuning-dataset-creator' },
                                                { label: 'ESG Report Creator', value: 'esg-report-creator' },
                                                { label: 'Cashflow Forecast', value: 'cashflow-forecast' },
                                                { label: 'Blogger', value: 'blogger' },
                                            ]}
                                        />
                                        <SelectInput
                                            label="Modello"
                                            name="model"
                                            options={modelOptions}
                                            defaultValue={agent?.model}
                                        />
                                    </div>
                                    <SelectInput
                                        label="Parent"
                                        name="parent"
                                        options={parentOptions}
                                        defaultValue={agent?.parent}
                                    />
                                    <TextInput
                                        label="Descrizione"
                                        placeholder="Descrizione"
                                        name="description"
                                        defaultValue={agent?.description}
                                    />
                                    <TextareaInput
                                        label="Istruzioni"
                                        placeholder="Istruzioni"
                                        name="instructions"
                                        defaultValue={agent?.instructions}
                                        rows={4}
                                    />

                                    <div className='md:flex gap-5 mt-4'>
                                        <TextInput
                                            label="Baseurl"
                                            placeholder="Baseurl"
                                            name="baseUrl"
                                            defaultValue={agent?.baseUrl}
                                        />
                                        <TextInput
                                            label="apiKey"
                                            placeholder="apiKey"
                                            name="apiKey"
                                            defaultValue={agent?.apiKey}
                                        />
                                    </div>
                                    
                                    <div className='pt-10'>
                                        <p className='text-sm block'>MCP Collegati</p>

                                        <div className='pt-2'>
                                            {agent?.mcps?.map(({ mcp, _id }) => {
                                                return (
                                                    <div key={mcp._id} className='w-full bg-verylight border border-black/6 rounded-md px-4 py-3 mb-3'>
                                                        <p className='text-sm'>{mcp.name}</p>
                                                        <p className='text-xs text-muted pt-1'>ID: {mcp._id}</p>
                                                        <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteMcpHandler(_id)}>
                                                            Rimuovi
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                                <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddMcpPopup(true)}>
                                                    <p className='text-sm text-muted'>+ Aggiungi MCP</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='pt-10'>
                                        <p className='text-sm block'>Tag Collegati</p>

                                        <div className='pt-2'>
                                            {agent?.tags?.map(({ tag, _id }) => {
                                                return (
                                                    <div key={tag._id} className='w-full bg-verylight border border-black/6 rounded-md px-4 py-3 mb-3'>
                                                        <p className='text-sm'>{tag.name}</p>
                                                        <p className='text-xs text-muted pt-1'>ID: {tag._id}</p>
                                                        <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteTagHandler(_id)}>
                                                            Rimuovi
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                                <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddTagPopup(true)}>
                                                    <p className='text-sm text-muted'>+ Aggiungi Tag</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='pt-10'>
                                        <p className='text-sm block'>Aree Collegate</p>

                                        <div className='pt-2'>
                                            {agent?.areas?.map(({ area, _id }) => {
                                                return (
                                                    <div key={area._id} className='w-full bg-verylight border border-black/6 rounded-md px-4 py-3 mb-3'>
                                                        <p className='text-sm'>{area.name}</p>
                                                        <p className='text-xs text-muted pt-1'>ID: {area._id}</p>
                                                        <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteAreaHandler(_id)}>
                                                            Rimuovi
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                                <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddAreaPopup(true)}>
                                                    <p className='text-sm text-muted'>+ Aggiungi Area</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='pt-10'>
                                        <p className='text-sm block'>Uffici Collegati</p>

                                        <div className='pt-2'>
                                            {agent?.offices?.map(({ office, _id }) => {
                                                return (
                                                    <div key={office._id} className='w-full bg-verylight border border-black/6 rounded-md px-4 py-3 mb-3'>
                                                        <p className='text-sm'>{office.name}</p>
                                                        <p className='text-xs text-muted pt-1'>ID: {office._id}</p>
                                                        <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteOfficeHandler(_id)}>
                                                            Rimuovi
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                            <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                                <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddOfficePopup(true)}>
                                                    <p className='text-sm text-muted'>+ Aggiungi Ufficio</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Container>
                    </div>
                </form>

                <AgentAddMCPPopup
                    visible={addMcpPopup}
                    setVisible={setAddMcpPopup}
                    present={agent?.mcps?.map(mcp => mcp._id)}
                />

                <AgentAddTagPopup
                    visible={addTagPopup}
                    setVisible={setAddTagPopup}
                    present={agent?.tags?.map(tag => tag._id)}
                />
                <AgentAddAreasPopup
                    visible={addAreaPopup}
                    setVisible={setAddAreaPopup}
                    present={agent?.areas?.map(area => area._id)}
                />
                <AgentAddOfficesPopup
                    visible={addOfficePopup}
                    setVisible={setAddOfficePopup}
                    present={agent?.offices?.map(office => office._id)}
                />
            </Page>
        </>
    )
}
